import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbarController } from "@rebasepro/app";
import {
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin
} from "@rebasepro/ui";

import { sinceLabel } from "./_prospects_shared";

const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

interface ClaimRow {
    id: string;
    auth_user_id: string;
    email?: string | null;
    contact_name?: string | null;
    contact_phone?: string | null;
    requested_services?: string[] | null;
    company_domain?: string | null;
    method: "signup" | "domain" | "review";
    status: "pending" | "approved" | "refused";
    decided_by?: string | null;
    decided_at?: string | null;
    created_at?: string | null;
}

const SERVICE_LABEL: Record<string, string> = {
    headhunting: "selección y contratación",
    attraction_optimization: "atracción y optimización de procesos"
};

/** "12 de septiembre de 2026". A decision is read later, when "hace 3 días" has stopped being true. */
function dayOf(iso: string | null | undefined): string {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

function who(claim: Pick<ClaimRow, "contact_name" | "email">): string {
    const name = (claim.contact_name ?? "").trim();
    const email = (claim.email ?? "").trim();
    if (name && email) return `${name} (${email})`;
    return name || email || "alguien sin nombre ni correo";
}

/** The part after the @, for "su correo es de …". */
function domainOf(email: string | null | undefined): string {
    return (email ?? "").split("@")[1]?.trim().toLowerCase() ?? "";
}

/**
 * The sentence that says how a company came to be managed, and by whom.
 *
 * Three different facts, and a "Reclamada: sí" would flatten them into one: a
 * person who created the record themselves, a person whose confirmed address
 * proved it, and a person somebody on the team decided to trust.
 */
function claimedSentence(claim: ClaimRow): string {
    const when = dayOf(claim.decided_at ?? claim.created_at);
    switch (claim.method) {
        case "signup":
            return `La registró ${who(claim)} desde la web el ${when}.`;
        case "domain":
            return claim.decided_by
                ? `La reclamó ${who(claim)}; lo aprobó ${claim.decided_by} el ${when}.`
                : `La reclamó ${who(claim)} el ${when}, con un correo confirmado de ${claim.company_domain ?? domainOf(claim.email)}.`;
        default:
            return `La reclamó ${who(claim)}; lo aprobó ${claim.decided_by ?? "alguien del equipo"} el ${when}.`;
    }
}

/**
 * Why nobody could settle this request automatically — the evidence the person
 * deciding has, and nothing presented as more than it is.
 */
function evidenceSentence(claim: ClaimRow, verified: boolean | undefined): string {
    const emailDomain = domainOf(claim.email);
    const onFile = (claim.company_domain ?? "").trim();
    const confirmed = verified === undefined
        ? "No sabemos si ha confirmado su correo."
        : verified ? "Ha confirmado su correo." : "Todavía no ha confirmado su correo.";
    if (claim.method === "domain") {
        return `Su correo es de ${emailDomain}, el dominio de esta ficha. En cuanto lo confirme y vuelva a entrar, ` +
            `la ficha pasa a ser suya sin que nadie haga nada; si lo apruebas ahora, se adelanta.`;
    }
    const why = onFile
        ? `Su correo es de ${emailDomain || "un dominio desconocido"} y esta ficha dice ${onFile}, así que el correo no lo demuestra.`
        : "Esta ficha no tiene un dominio con el que comprobar su correo.";
    return `${why} ${confirmed}`;
}

/**
 * Who manages this company, how they came to, and who is asking to.
 *
 * Its own file rather than more of CompanyOverview, which carries the cover's
 * other five sections. It reads `company_claims` (drizzle/0062): the trigger's
 * `signup` rows, `claim-company`'s `domain` and `review` rows, and whatever
 * the team decided about them. Approving and refusing go through
 * `functions/decide-company-claim`, which writes the membership and the history
 * in one transaction — nothing here writes a row itself.
 *
 * The address shown is the one on the claimant's account, read by the server
 * when they asked; it says so, because where an address came from is never
 * hidden (PRODUCT.md).
 */
export function CompanyClaimsSection({
    client,
    companyId,
    onDecided
}: {
    client: any;
    companyId: string;
    /** Reload the rest of the cover: a decision changes its members and next step. */
    onDecided?: () => void | Promise<void>;
}) {
    const data = client?.data;
    const snackbar = useSnackbarController();
    const [claims, setClaims] = useState<ClaimRow[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [verified, setVerified] = useState<Record<string, boolean>>({});
    const [busy, setBusy] = useState<string | null>(null);
    const wanted = useRef<string | null>(null);

    const load = useCallback(async () => {
        if (!data) return;
        wanted.current = companyId;
        try {
            const page = await data.collection("company-claims").find({
                where: { company_id: ["==", companyId] },
                orderBy: ["created_at", "desc"],
                limit: 50
            });
            if (wanted.current !== companyId) return;
            const rows = (page?.data ?? []) as ClaimRow[];
            setClaims(rows);
            setFailed(false);

            // Whether each person asking has confirmed their address. Only
            // asked for open requests — that is where it changes a decision.
            const askers = [...new Set(rows.filter(r => r.status === "pending").map(r => r.auth_user_id))];
            if (askers.length > 0) {
                const users = await data.collection("users")
                    .find({ where: { id: ["in", askers] }, limit: askers.length })
                    .catch(() => undefined);
                if (wanted.current !== companyId) return;
                const map: Record<string, boolean> = {};
                for (const u of (users?.data ?? []) as { id: unknown; emailVerified?: unknown }[]) {
                    map[String(u.id)] = u.emailVerified === true;
                }
                setVerified(map);
            }
        } catch {
            if (wanted.current !== companyId) return;
            setClaims(null);
            setFailed(true);
        }
    }, [data, companyId]);

    useEffect(() => {
        setClaims(null);
        setFailed(false);
        setVerified({});
        void load();
    }, [load]);

    const decide = useCallback(async (claim: ClaimRow, decision: "approve" | "refuse") => {
        const question = decision === "approve"
            ? `¿Dar a ${who(claim)} acceso a esta empresa? Verá sus vacantes y las candidaturas que reciban.`
            : `¿Rechazar la solicitud de ${who(claim)}? No se le avisa: si quieres explicárselo, escríbele desde Correo.`;
        if (!window.confirm(question)) return;

        setBusy(claim.id);
        try {
            const token = await client?.resolveToken?.();
            const res = await fetch(`${client?.baseUrl ?? ""}${client?.apiPath ?? "/api"}/functions/decide-company-claim`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ claim_id: claim.id, decision })
            });
            const json = await res.json().catch(() => ({})) as {
                error?: { message?: string } | string;
                welcome_sent?: boolean;
            };
            if (!res.ok) {
                const message = typeof json.error === "string" ? json.error : json.error?.message;
                throw new Error(message || `Error ${res.status}`);
            }
            snackbar.open({
                type: "success",
                message: decision === "approve"
                    ? (json.welcome_sent
                        ? "Aprobada. Le hemos enviado el correo de bienvenida."
                        : "Aprobada. El correo de bienvenida no salió: escríbele desde Correo.")
                    : "Rechazada."
            });
            await load();
            await onDecided?.();
        } catch (err) {
            snackbar.open({ type: "error", message: err instanceof Error ? err.message : "No se pudo guardar la decisión." });
        } finally {
            setBusy(null);
        }
    }, [client, load, onDecided, snackbar]);

    if (failed) {
        return (
            <Typography variant="body2" color="secondary">
                No pudimos leer quién gestiona esta empresa ni si alguien lo ha pedido.
            </Typography>
        );
    }
    if (!claims || claims.length === 0) return null;

    const pending = claims.filter(c => c.status === "pending");
    const approved = claims.find(c => c.status === "approved") ?? null;
    const refused = claims.filter(c => c.status === "refused");

    return (
        <div className="flex flex-col gap-2">
            {approved && (
                <Typography variant="body2" color="secondary">{claimedSentence(approved)}</Typography>
            )}

            {pending.map(claim => {
                const services = (claim.requested_services ?? []).map(s => SERVICE_LABEL[s] ?? s).join(", ");
                return (
                    <Card key={claim.id} className={cls("p-4 flex flex-col gap-2", defaultBorderMixin)}>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Typography variant="micro" color="secondary">Pide gestionar esta ficha</Typography>
                            <Chip size="small" colorScheme={claim.method === "domain" ? "blueLighter" : "orangeLighter"}>
                                {claim.method === "domain" ? "Falta que confirme su correo" : "Pendiente de decidir"}
                            </Chip>
                        </div>
                        <Typography variant="body2" className="font-medium">
                            {(claim.contact_name ?? "").trim() || "(sin nombre)"}
                            {claim.contact_phone ? <span className="font-normal opacity-70"> · {claim.contact_phone}</span> : null}
                        </Typography>
                        {claim.email && (
                            <Typography variant="body2">
                                <a href={`/correo?para=${encodeURIComponent(claim.email)}`} className="text-primary hover:underline">{claim.email}</a>
                                <span className="opacity-70"> — el correo de su cuenta</span>
                            </Typography>
                        )}
                        <Typography variant="body2" color="secondary">
                            {evidenceSentence(claim, verified[claim.auth_user_id])}
                        </Typography>
                        <Typography variant="body2" color="secondary">
                            {`Lo pidió ${sinceLabel(claim.created_at)} desde la web${services ? `, marcando ${services}` : ""}.`}
                        </Typography>
                        <div className="flex gap-2 flex-wrap pt-1">
                            <Button size="small" variant="filled" disabled={busy === claim.id} onClick={() => decide(claim, "approve")}>
                                Aprobar
                            </Button>
                            <Button size="small" variant="outlined" disabled={busy === claim.id} onClick={() => decide(claim, "refuse")}>
                                Rechazar
                            </Button>
                        </div>
                    </Card>
                );
            })}

            {refused.map(claim => (
                <Typography key={claim.id} variant="body2" color="secondary">
                    {`Rechazada la solicitud de ${who(claim)}: lo decidió ${claim.decided_by ?? "alguien del equipo"} el ${dayOf(claim.decided_at)}.`}
                </Typography>
            ))}
        </div>
    );
}
